import React from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { CalendarDays, Plus, Shield, ChevronDown, MapPin } from "lucide-react";
import { DEFAULT_CITY } from "@/constants/cities";
import { useCities } from "@/context/CitiesContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Navbar() {
  const { pathname } = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const { cities } = useCities();
  const city = params.city || DEFAULT_CITY;
  const current = cities.find((c) => c.slug === city);
  const cityName = current?.name || city.charAt(0).toUpperCase() + city.slice(1);

  const linkCls = (active) =>
    `overline transition-colors ${active ? "text-ec-volt" : "text-zinc-400 hover:text-white"}`;

  return (
    <header className="sticky top-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to={`/${city}`} data-testid="nav-logo" className="flex items-center gap-2.5 group">
            <span className="grid place-items-center w-9 h-9 bg-ec-volt text-black">
              <CalendarDays size={20} strokeWidth={2.5} />
            </span>
            <span className="font-heading text-2xl tracking-wide leading-none">
              EVENT CITY<span className="text-ec-volt">.</span>
            </span>
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="city-switcher" className="hidden sm:inline-flex items-center gap-1.5 overline text-zinc-300 border border-white/15 px-2.5 py-1.5 ml-1 hover:border-ec-volt hover:text-ec-volt transition-colors">
                <MapPin size={13} /> {cityName} <ChevronDown size={13} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-ec-surface border-white/10 text-white">
              {cities.map((c) => (
                <DropdownMenuItem
                  key={c.slug}
                  data-testid={`city-option-${c.slug}`}
                  onClick={() => navigate(`/${c.slug}`)}
                  className="overline cursor-pointer focus:bg-ec-volt focus:text-black"
                >
                  {c.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <nav className="flex items-center gap-5 sm:gap-7">
          <Link to={`/${city}`} data-testid="nav-calendar" className={linkCls(pathname === `/${city}`)}>
            Calendar
          </Link>
          <Link to={`/${city}/submit`} data-testid="nav-submit" className={linkCls(pathname === `/${city}/submit`)}>
            <span className="inline-flex items-center gap-1.5">
              <Plus size={14} /> Submit
            </span>
          </Link>
          <Link
            to="/login"
            data-testid="nav-admin"
            className="inline-flex items-center gap-1.5 overline border border-white/15 px-3 py-2 hover:border-ec-volt hover:text-ec-volt transition-colors text-zinc-300"
          >
            <Shield size={14} /> Login
          </Link>
        </nav>
      </div>
    </header>
  );
}
